import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import {
  TenantJoinMeResponseSchema,
  TenantJoinRequestResponseSchema,
} from './types';
import type {
  TenantJoinMeResponse,
  TenantJoinRequestResponse,
} from './types';

/**
 * CEC テナント参加申請 API のインターフェース定義
 *
 * Backstage backend の tenant-invitation プラグインを呼び出す。
 * CEC テナント未所属のユーザーが利用する画面のため、
 * Entra (Microsoft) のアクセストークンには依存しない。
 */
export interface TenantInvitationApi {
  /** ログインユーザーの CEC テナント所属状態を取得する */
  getMe(): Promise<TenantJoinMeResponse>;
  /** CEC テナントへの参加（ゲスト招待）を申請する */
  requestJoin(): Promise<TenantJoinRequestResponse>;
}

export const tenantInvitationApiRef = createApiRef<TenantInvitationApi>().with({
  id: 'plugin.idp-control-base.tenant-invitation',
});

/** サーバーが返すエラーメッセージを取り出せなかった場合のフォールバック文言 */
const FALLBACK_MESSAGES: Record<number, string> = {
  401: 'ログインセッションが切れています。再ログインしてからやり直してください。',
  409: 'すでに CEC テナントに招待済みまたは所属済みです。',
  422: 'ログイン情報からメールアドレスを取得できませんでした。管理者に問い合わせてください。',
  429: '申請が短時間に集中しています。しばらく待ってからやり直してください。',
};

export class TenantInvitationClient implements TenantInvitationApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('tenant-invitation');
  }

  /** エラーレスポンスから表示用メッセージを組み立てる */
  private async toError(response: Response): Promise<Error> {
    let detail: string | undefined;
    try {
      const json = (await response.json()) as { error?: unknown };
      if (typeof json.error === 'string') {
        detail = json.error;
      }
    } catch {
      // JSON でないレスポンスはフォールバック文言を使う
    }
    const message =
      detail ??
      FALLBACK_MESSAGES[response.status] ??
      `テナント参加申請 API でエラーが発生しました (${response.status} ${response.statusText})`;
    return new Error(message);
  }

  async getMe(): Promise<TenantJoinMeResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/me`);
    if (!response.ok) {
      throw await this.toError(response);
    }
    return TenantJoinMeResponseSchema.parse(await response.json());
  }

  async requestJoin(): Promise<TenantJoinRequestResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
    });
    if (!response.ok) {
      throw await this.toError(response);
    }
    return TenantJoinRequestResponseSchema.parse(await response.json());
  }
}
